import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  CREATOR_SUBMISSION_STATES,
  FRAUD_CHECKS,
  PLATFORM_LABELS,
  SUBMISSIONS_PAGE_SIZE,
  SUBMISSION_SORTS,
  SUBMISSION_SORT_LABELS,
  type CreatorSubmissionState,
  type CreatorTotals,
  type Platform,
  type Submission,
  type SubmissionListResponse,
  type SubmissionSort,
  reReviewPrefill,
} from '@nexus/shared';

import { apiFetch, apiGet } from '../lib/api';
import { reviewPromise, usePublicConfig } from '../lib/publicConfig';
import { CheckList } from '../components/CheckList';
import {
  Alert,
  Badge,
  Button,
  Card,
  checkName,
  DataTable,
  EmptyState,
  ExternalLink,
  Fact,
  Facts,
  LoadErrorPage,
  LoadingPage,
  Money,
  Num,
  FieldRow,
  PageHeader,
  SelectField,
  Stat,
  StatGrid,
  statusTone,
  submissionState,
  submissionStateLabel,
  submissionStateNote,
  submissionStateTone,
  TextField,
  When,
  type Column,
} from '../ui';

/**
 * My submissions.
 *
 * ## What this screen is for
 *
 * Phase 3 put the status, the checks and the reason here. Phase 4 added the
 * readings, the verified figure and the earning. What was still missing is the
 * thing a creator does with a history once they have more than a handful of
 * rows: FIND one. So the page now searches, filters, sorts and pages — and
 * every one of those happens in SQL, not here.
 *
 * ## Why the query is in the URL
 *
 * The sort, the filter and the search term live in the query string, so a
 * reload keeps them, the back button works, and a creator can send somebody
 * (us, in a support conversation) the exact view they are looking at. A sort
 * held in `useState` is a sort that vanishes on every refresh, which is when
 * people are most likely to refresh: while they are waiting on a decision.
 *
 * ## Why the totals are not computed from the rows
 *
 * They used to be, by summing the submissions on screen. That was wrong twice
 * over: it counted only the page, and it would shrink the moment a filter was
 * applied — a "total earned" that changes when you click "Rejected" is a total
 * nobody can trust. The API sends lifetime figures for the whole creator, out
 * of the same function the Wallet's figures come from, and a test compares the
 * two responses to the cent.
 *
 * ## Two rules this screen keeps
 *
 * A null view count is NOT OBSERVED and renders as a dash, never as 0 — an
 * Instagram photo publishes no play count and a YouTube owner can hide theirs,
 * and "0" would tell a creator their clip collapsed. And a held earning shows
 * the rule that held it in the rule's own words, because "held for review"
 * with no sentence is how a pilot loses people.
 */

const SORT_VALUES: readonly string[] = SUBMISSION_SORTS;
const STATE_VALUES: readonly string[] = CREATOR_SUBMISSION_STATES;

/** What the URL says, defended against anything a person can type into it. */
function readQuery(params: URLSearchParams): {
  q: string;
  status: CreatorSubmissionState | '';
  sort: SubmissionSort;
} {
  const sort = params.get('sort') ?? '';
  const status = params.get('status') ?? '';
  return {
    q: params.get('q') ?? '',
    status: STATE_VALUES.includes(status) ? (status as CreatorSubmissionState) : '',
    sort: SORT_VALUES.includes(sort) ? (sort as SubmissionSort) : 'newest',
  };
}

export function Submissions(): React.JSX.Element {
  const config = usePublicConfig();
  const [params, setParams] = useSearchParams();
  const { q, status, sort } = readQuery(params);

  const [rows, setRows] = useState<Submission[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState<CreatorTotals | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloading, setDownloading] = useState(false);
  /* Set when the last export hit the 1,000-row cap, so the page can say so. */
  const [truncated, setTruncated] = useState(false);
  /* Bumped by "Try again": part of the effect's key, so a retry is a re-run of
     exactly the request that failed. */
  const [attempt, setAttempt] = useState(0);

  /**
   * The text box is local and the URL follows it after a pause.
   *
   * Writing every keystroke into the URL would put eight history entries
   * behind one word and fire eight requests. `replace` rather than push for
   * the same reason: typing is not navigation.
   */
  const [text, setText] = useState(q);
  const typed = useRef(false);
  useEffect(() => {
    /* The URL is the source of truth whenever it changed on its own — the back
       button, or a link. Only a keystroke starts the debounce. */
    if (!typed.current) setText(q);
  }, [q]);

  useEffect(() => {
    if (!typed.current) return;
    const timer = setTimeout(() => {
      typed.current = false;
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (text) next.set('q', text);
          else next.delete('q');
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [text, setParams]);

  function setParam(key: string, value: string): void {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }

  /** The query string the API is asked for, built once for both loads. */
  const queryFor = useCallback(
    (offset: number): string => {
      const search = new URLSearchParams();
      if (q) search.set('q', q);
      if (status) search.set('status', status);
      search.set('sort', sort);
      search.set('limit', String(SUBMISSIONS_PAGE_SIZE));
      if (offset > 0) search.set('offset', String(offset));
      return search.toString();
    },
    [q, status, sort],
  );

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    void (async () => {
      try {
        const body = await apiGet<SubmissionListResponse>(`/submissions?${queryFor(0)}`);
        if (cancelled) return;
        setRows(body.submissions);
        setTotal(body.total);
        setTotals(body.totals);
        setError(null);
      } catch {
        if (!cancelled) setError('Could not load your submissions.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryFor, attempt]);

  /**
   * Download what is on screen, as a spreadsheet.
   *
   * The same query string the list was fetched with, so the file is the view
   * the creator is looking at rather than a second answer to "my submissions".
   * It goes through `fetch` and a blob rather than a plain link because the
   * API is on another origin behind a session cookie: a bare `<a href>` would
   * navigate there without credentials and download a 401.
   */
  async function downloadCsv(): Promise<void> {
    setDownloading(true);
    setTruncated(false);
    try {
      const response = await apiFetch(`/submissions.csv?${queryFor(0)}`);
      if (!response.ok) throw new Error('export failed');
      /* The export stops at a thousand rows, and a creator reconciling their
         own money has to be told that BEFORE they add the column up. The file
         carries the sentence too — this is the half they see without opening
         it. `corsMiddleware` exposes the header; without that the browser
         hides it on a cross-origin fetch and this is silently always false. */
      setTruncated(response.headers.get('x-nexus-export-truncated') === 'true');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `nexus-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      /* Revoked, or every export leaks its blob for the life of the tab. */
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not build the download.');
    } finally {
      setDownloading(false);
    }
  }

  async function loadMore(): Promise<void> {
    if (rows === null) return;
    setLoadingMore(true);
    try {
      const body = await apiGet<SubmissionListResponse>(`/submissions?${queryFor(rows.length)}`);
      /* Appended by id rather than by concatenation alone: a submission that
         was approved between the two requests can move across the page
         boundary and arrive twice, and React would then warn about a duplicate
         key on a list the creator is reading. */
      setRows((previous) => {
        const seen = new Set((previous ?? []).map((r) => r.id));
        return [...(previous ?? []), ...body.submissions.filter((r) => !seen.has(r.id))];
      });
      setTotal(body.total);
      setTotals(body.totals);
    } catch {
      setError('Could not load more submissions.');
    } finally {
      setLoadingMore(false);
    }
  }

  if (error && rows === null) {
    return (
      <LoadErrorPage
        eyebrow="Creator"
        title="My submissions"
        heading="Could not load your submissions."
        onRetry={() => {
          setError(null);
          setAttempt((a) => a + 1);
        }}
      >
        <span>Nothing you have submitted is affected by this.</span>
      </LoadErrorPage>
    );
  }

  if (rows === null) {
    return <LoadingPage eyebrow="Creator" title="My submissions" stats />;
  }

  const filtered = Boolean(q || status);
  const nothingAtAll = total === 0 && !filtered;

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="My submissions"
        lead={
          <>
            Every clip you have submitted, where it stands, and every reading behind the figure you
            are paid on. Anything in review is looked at{' '}
            <span data-testid="review-promise">{reviewPromise(config?.reviewSlaHours)}</span>.
          </>
        }
        actions={
          <>
            <Link className="btn btn-primary" to="/submit">
              Submit a clip
            </Link>
            {!nothingAtAll && (
              <Button onClick={() => void downloadCsv()} disabled={downloading}>
                {downloading ? 'Building…' : 'Download CSV'}
              </Button>
            )}
          </>
        }
      />

      {/**
       * TOTAL EARNED FIRST, and lifetime.
       *
       * It is the number a creator opens this page for, it is the same number
       * the Wallet shows, and it does not move when a filter does.
       */}
      {totals && (
        <Card variant="feature">
          <StatGrid>
            <Stat
              label="Total earned"
              value={<Money cents={totals.lifetimeEarnedCents} />}
              note="Every earning that was not voided."
              emphasis
            />
            <Stat
              label="Paid to date"
              value={<Money cents={totals.paidCents} />}
              note="What has actually left."
            />
            <Stat
              label="Pending"
              value={<Money cents={totals.pendingCents} />}
              note="Owed, but inside the holdback or held by a check."
            />
            <Stat
              label="Submissions"
              value={<Num value={total} />}
              note={filtered ? 'Matching your filter.' : 'Everything you have submitted.'}
            />
          </StatGrid>
          <p className="muted">
            These are lifetime figures for your whole account, and they are the same sums the{' '}
            <Link to="/wallet">Wallet</Link> shows.
          </p>
        </Card>
      )}

      {truncated && (
        <Alert tone="waiting" title="That download stopped at 1,000 rows">
          <span>
            Your history is longer than one export. Narrow it — by campaign, by status, or with
            the search box — and download again to get the rest. The file says so on its last
            line too.
          </span>
        </Alert>
      )}

      {!nothingAtAll && (
        <Card label="Find a submission">
          <FieldRow>
            <TextField
              label="Search"
              name="q"
              type="search"
              value={text}
              onChange={(event) => {
                typed.current = true;
                setText(event.target.value);
              }}
              hint="Campaign name, the post link, or the account you posted from."
            />
            <SelectField
              label="Status"
              name="status"
              value={status}
              onChange={(event) => setParam('status', event.target.value)}
            >
              <option value="">Any status</option>
              {CREATOR_SUBMISSION_STATES.map((state) => (
                <option key={state} value={state}>
                  {submissionStateLabel(state)}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Sort by"
              name="sort"
              value={sort}
              onChange={(event) => setParam('sort', event.target.value)}
            >
              {SUBMISSION_SORTS.map((value) => (
                <option key={value} value={value}>
                  {SUBMISSION_SORT_LABELS[value]}
                </option>
              ))}
            </SelectField>
          </FieldRow>
          {status && <p className="muted">{submissionStateNote(status)}</p>}
        </Card>
      )}

      {nothingAtAll ? (
        <Card>
          <EmptyState
            headingLevel={2}
            title="Nothing submitted yet."
            action={
              <Link className="btn btn-primary" to="/submit">
                Paste your first link
              </Link>
            }
          >
            Cut a clip from a campaign's source material, post it on your own account, then paste
            the link. The checks run in seconds.
          </EmptyState>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            headingLevel={2}
            title="Nothing matches that."
            action={
              <Button
                onClick={() => {
                  typed.current = false;
                  setText('');
                  setParams(new URLSearchParams());
                }}
              >
                Clear the filter
              </Button>
            }
          >
            You have {total === 0 ? 'submissions' : `${total} submissions`} in total — none of them
            match this search.
          </EmptyState>
        </Card>
      ) : null}

      {rows.map((submission) => (
        <SubmissionCard key={submission.id} submission={submission} />
      ))}

      {rows.length < total && (
        <Card>
          <div className="actions">
            <Button onClick={() => void loadMore()} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : `Load more (${total - rows.length} left)`}
            </Button>
            <span className="muted">
              Showing {rows.length} of {total}.
            </span>
          </div>
          {error && <Alert tone="critical">{error}</Alert>}
        </Card>
      )}
    </>
  );
}

function SubmissionCard({ submission }: { submission: Submission }): React.JSX.Element {
  const platform = PLATFORM_LABELS[submission.platform as Platform] ?? submission.platform;
  /* The word the creator reads, from the shared map: the same rule the API's
     `?status=` filter uses, so the pill and the filter cannot disagree. */
  const state = submissionState(submission);

  return (
    <Card
      label={submission.postUrl}
      eyebrow={
        <>
          {platform} <span aria-hidden="true">·</span> {submission.accountHandle ?? 'account'}
        </>
      }
      title={submission.campaignName}
      variant={state === 'REJECTED' ? 'dimmed' : undefined}
      aside={
        <Badge tone={submissionStateTone(state)} testId="submission-state">
          {submissionStateLabel(state)}
        </Badge>
      }
    >
      <p className="muted">
        <ExternalLink href={submission.postUrl} />
      </p>

      {submission.rejectionReason && (
        <Alert tone="critical">
          <span>{submission.rejectionReason}</span>
          {/* The door to a person, on the row that needs one. The form arrives
              with the submission and the sentence above already in it. */}
          <Link to={reReviewHref(submission)} data-testid="request-rereview">
            Request a re-review
          </Link>
        </Alert>
      )}
      {submission.holdReason && <Alert tone="waiting">{submission.holdReason}</Alert>}
      {submission.visibilityLostAt && (
        <Alert tone="critical" title="This post can no longer be counted">
          <span>
            {submission.visibilityLostReason === 'private'
              ? 'It is no longer public.'
              : submission.visibilityLostReason === 'unlisted'
                ? 'It is unlisted, so nobody can find it and its views cannot be counted.'
                : 'It is no longer on the platform.'}
          </span>
        </Alert>
      )}
      {submission.measurementClosedAt && !submission.visibilityLostAt && (
        /* Why a verified post shows no figure. Without this the creator is left
           looking at an empty readings table with nothing saying why. */
        <Alert tone="waiting">{submission.measurementClosedReason}</Alert>
      )}

      <Facts>
        <Fact label="Posted">
          <When value={submission.postPublishedAt} withTime />
        </Fact>
        <Fact label="Submitted">
          <When value={submission.submittedAt} withTime />
        </Fact>
        <Fact label="Latest views">
          {/* The most recent reading, which for the first three days of a
              clip's life is the ONLY view count there is: the verified figure
              does not exist until the window closes. `null` is NOT OBSERVED,
              never zero. */}
          <Num value={submission.latestViews} fallback="Not read yet" />
        </Fact>
        <Fact label="Account">{submission.accountHandle ?? '—'}</Fact>
      </Facts>

      {submission.verifiedViews !== null && (
        <StatGrid>
          <Stat
            label="Verified views"
            value={<Num value={submission.verifiedViews} />}
            note="The figure the money was computed from."
            emphasis
          />
          <Stat
            label="Earning"
            value={<Money cents={submission.earning?.amountCents ?? 0} />}
            note={submission.earning ? undefined : 'Nothing to pay on this one.'}
          />
          <Stat
            label="Where it is"
            value={
              submission.earning ? (
                <Badge tone={statusTone(submission.earning.status, 'earning')}>
                  {submission.earning.statusLabel}
                </Badge>
              ) : (
                <Badge tone="neutral">Nothing to pay</Badge>
              )
            }
          />
        </StatGrid>
      )}

      {submission.earning?.heldReason && (
        <Alert tone="waiting" title={checkName(submission.earning.heldCheck)}>
          <span>{submission.earning.heldReason} An operator will look at it.</span>
        </Alert>
      )}
      {submission.earning?.voidReason && (
        <Alert tone="critical">{submission.earning.voidReason}</Alert>
      )}

      <Readings submission={submission} />

      <details className="reveal">
        <summary>The checks behind this submission</summary>
        <div className="stack tight">
          <CheckList checks={submission.checks} stoppedAt={stopOf(submission)} />
          <FraudChecks submission={submission} />
        </div>
      </details>
    </Card>
  );
}

/**
 * Every reading, by bucket.
 *
 * A FAILURE row is shown as a failure rather than hidden: a creator who can
 * see that we could not read their post at hour 24 understands the gap in the
 * table, and one who cannot assumes the worst.
 */
function Readings({ submission }: { submission: Submission }): React.JSX.Element | null {
  if (submission.readings.length === 0) return null;

  type Reading = Submission['readings'][number];
  const columns: Array<Column<Reading>> = [
    { key: 'at', header: 'At', cell: (r) => r.label, primary: true },
    {
      key: 'views',
      header: 'Views',
      numeric: true,
      align: 'end',
      cell: (r) =>
        r.captureStatus !== 'SUCCESS' ? (
          <span className="tone-critical">could not be read</span>
        ) : (
          /* null is NOT OBSERVED, never zero. */
          <Num value={r.views} />
        ),
    },
    { key: 'likes', header: 'Likes', numeric: true, align: 'end', cell: (r) => <Num value={r.likes} /> },
    {
      key: 'comments',
      header: 'Comments',
      numeric: true,
      align: 'end',
      cell: (r) => <Num value={r.comments} />,
    },
    {
      key: 'taken',
      header: 'Taken',
      cell: (r) => (
        <span className="muted">
          <When value={r.capturedAt} withTime />
        </span>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={submission.readings}
      getKey={(r, i) => `${r.capturedAt}-${i}`}
      caption="Readings, timed from when the post went live"
      empty={null}
    />
  );
}

/**
 * The checks that ran when the window closed, each with the sentence it wrote.
 *
 * Separate from `CheckList`, which renders phase 3's fixed order of ownership
 * and timing checks. These are named by the PLAN's name, carried in each row's
 * evidence — two of them share one stored `checkType` because the scaffold's
 * constraint has one value for both, and the evidence is what tells them
 * apart.
 */
function FraudChecks({ submission }: { submission: Submission }): React.JSX.Element | null {
  const names = new Set<string>(FRAUD_CHECKS.map((c) => c.planName));
  const rows = submission.checks.filter((check) => {
    const evidence = check.evidence as { check?: unknown } | null;
    return typeof evidence?.check === 'string' && names.has(evidence.check);
  });
  if (rows.length === 0) return null;

  return (
    <ol className="checklist">
      {rows.map((check) => {
        const name = (check.evidence as { check: string }).check;
        return (
          <li key={check.id} data-state={check.result === 'PASS' ? 'pass' : 'fail'}>
            <span className="mark" aria-hidden="true">
              {check.result === 'PASS' ? '✓' : '✕'}
            </span>
            <span>
              <span className="check-label">{checkName(name)}</span>
              <div className="check-note">{check.note}</div>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** The check a finished run stopped at: the first that did not pass. */
function stopOf(submission: Submission): string | null {
  const failed = submission.checks.find(
    (check) => check.checkType !== 'CONTENT_REVIEW' && check.result !== 'PASS',
  );
  /* The PLAN name: `ACCOUNT_MATCH` is two rules and the column cannot tell
     ownership from the follower floor. */
  return failed?.planName ?? null;
}

/**
 * Where "Request a re-review" goes: the new-ticket form, prefilled with this
 * submission, its campaign and platform in the subject, and the rejection
 * sentence quoted in the body so the creator only has to add why.
 */
function reReviewHref(submission: Submission): string {
  const prefill = reReviewPrefill({
    campaignName: submission.campaignName,
    platformLabel: PLATFORM_LABELS[submission.platform as Platform] ?? submission.platform,
    rejectionReason: submission.rejectionReason,
  });
  const params = new URLSearchParams({
    category: 'SUBMISSION_REVIEW',
    refType: 'PostSubmission',
    refId: submission.id,
    subject: prefill.subject,
    body: prefill.body,
  });
  return `/support/new?${params.toString()}`;
}
